import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Loader2, Plus, Save, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import {
  AutonomousCommunityLabel,
  type AutonomousCommunityT,
  type CourtHolidayDto,
  type Firm,
} from '@toga/shared'
import { useAuth } from '../../contexts/AuthContext'

export default function LitigationSettingsPage() {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'

  const [firm, setFirm] = useState<Firm | null>(null)
  const [community, setCommunity] = useState<AutonomousCommunityT | ''>('')
  const [savingFirm, setSavingFirm] = useState(false)
  const [holidays, setHolidays] = useState<CourtHolidayDto[]>([])
  const [loadingHolidays, setLoadingHolidays] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const loadFirm = useCallback(async () => {
    try {
      const { firm } = await api.get<{ firm: Firm }>('/firm')
      setFirm(firm)
      setCommunity(firm.autonomousCommunity ?? '')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    }
  }, [])

  const loadHolidays = useCallback(async () => {
    setLoadingHolidays(true)
    try {
      const { items } = await api.get<{ items: CourtHolidayDto[] }>('/court-holidays')
      setHolidays(items)
    } catch {
      setHolidays([])
    } finally {
      setLoadingHolidays(false)
    }
  }, [])

  useEffect(() => {
    void loadFirm()
    void loadHolidays()
  }, [loadFirm, loadHolidays])

  const onSaveCommunity = async (e: FormEvent) => {
    e.preventDefault()
    if (!firm) return
    setSavingFirm(true)
    setError(null)
    setOkMsg(null)
    try {
      await api.patch('/firm', {
        autonomousCommunity: community || null,
      })
      setOkMsg('Comunidad autónoma actualizada. Los plazos se calcularán con sus festivos.')
      await loadFirm()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? 'Error')
    } finally {
      setSavingFirm(false)
    }
  }

  const onDelete = async (id: string) => {
    if (!confirm('¿Eliminar este festivo?')) return
    try {
      await api.delete(`/court-holidays/${id}`)
      await loadHolidays()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  if (!firm) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Cargando…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">Procesal</h2>
        <p className="t-muted text-sm mt-1">
          Comunidad autónoma del bufete y festivos locales para el cómputo de plazos en
          días hábiles.
        </p>
      </header>

      <section className="surface p-5 space-y-3">
        <form onSubmit={onSaveCommunity} className="space-y-3 max-w-md">
          <label className="block">
            <span className="text-sm t-secondary">Comunidad autónoma</span>
            <select
              value={community}
              onChange={(e) => setCommunity(e.target.value as AutonomousCommunityT | '')}
              disabled={!isAdmin || savingFirm}
              className="w-full mt-1.5 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 disabled:opacity-60"
            >
              <option value="">— sin especificar (solo festivos nacionales) —</option>
              {Object.entries(AutonomousCommunityLabel).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <p className="text-xs t-faint mt-1">
              Determina qué festivos autonómicos cuentan como inhábiles.
            </p>
          </label>

          {error && <p className="text-xs text-rose-300">{error}</p>}
          {okMsg && <p className="text-xs text-emerald-300">{okMsg}</p>}

          {isAdmin && (
            <button
              type="submit"
              disabled={savingFirm}
              className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {savingFirm ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar
            </button>
          )}
        </form>
      </section>

      <section className="surface p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="t-primary font-semibold">Festivos extra</h3>
            <p className="text-xs t-muted mt-0.5">
              Festivos locales del partido judicial u otros días inhábiles que no estén ya
              cubiertos por los nacionales/autonómicos.
            </p>
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
            >
              <Plus size={12} /> Añadir
            </button>
          )}
        </div>

        {loadingHolidays ? (
          <div className="t-muted text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : holidays.length === 0 ? (
          <p className="text-sm t-faint">Sin festivos personalizados.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider t-faint">
              <tr>
                <th className="text-left py-1.5">Fecha</th>
                <th className="text-left">Descripción</th>
                <th className="text-left">Ámbito</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.id} className="border-t border-base/40">
                  <td className="py-2 font-mono t-secondary">{h.date}</td>
                  <td className="py-2 t-primary">{h.description}</td>
                  <td className="py-2 text-xs t-faint">
                    {h.community ? AutonomousCommunityLabel[h.community] : 'Todo el bufete'}
                  </td>
                  <td className="py-2 text-right">
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => void onDelete(h.id)}
                        className="t-muted hover:text-rose-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showNew && (
        <NewHolidayModal
          onClose={() => setShowNew(false)}
          onSaved={async () => {
            setShowNew(false)
            await loadHolidays()
          }}
        />
      )}
    </div>
  )
}

function NewHolidayModal({
  onClose,
  onSaved,
}: {
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [date, setDate] = useState('')
  const [description, setDescription] = useState('')
  const [community, setCommunity] = useState<AutonomousCommunityT | ''>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!date || !description.trim()) {
      setError('Indica fecha y descripción')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post('/court-holidays', {
        date,
        description: description.trim(),
        community: community || null,
      })
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">Nuevo festivo</h3>
        <label className="block">
          <span className="text-xs t-muted">Fecha</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            required
          />
        </label>
        <label className="block">
          <span className="text-xs t-muted">Descripción</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Fiesta local de…"
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            required
          />
        </label>
        <label className="block">
          <span className="text-xs t-muted">Aplica a</span>
          <select
            value={community}
            onChange={(e) => setCommunity(e.target.value as AutonomousCommunityT | '')}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          >
            <option value="">Todo el bufete</option>
            {Object.entries(AutonomousCommunityLabel).map(([k, v]) => (
              <option key={k} value={k}>
                Solo si CCAA = {v}
              </option>
            ))}
          </select>
        </label>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Crear
          </button>
        </div>
      </form>
    </div>
  )
}
